import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';

const CONFIRM_STEPS = [
  {
    emoji: '🚨',
    title: 'WAIT.',
    body: "This site was built by a vibe coder who just prompts AI to build stuff. No guarantees on functionality — only on the fact that he's a genuinely good dude who means well.",
    yes: "I live for chaos",
    no: "I need more sleep too",
  },
  {
    emoji: '📬',
    title: 'A small confession...',
    body: "I will technically receive your email address. I won't do anything weird with it. Probably. I mean definitely. It just sits there looking pretty.",
    yes: "My email is ugly anyway",
    no: "My email is sacred",
  },
  {
    emoji: '🤝',
    title: 'One last thing.',
    body: "By clicking yes, you agree that this is a cheeky side project, you acknowledge the vibes, and you accept that something mildly unexpected may or may not happen.",
    yes: "I accept the vibes",
    no: "The vibes are off",
  },
];

export default function Login() {
  const { loginWithGoogle, loginWithMicrosoft, isAuthenticated, isLoading, error, clearError } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [confirmStep, setConfirmStep] = useState<null | { step: number; provider: 'google' | 'microsoft' }>(null);
  const [isPending, setIsPending] = useState(false);
  const [storedAuthError, setStoredAuthError] = useState<string | null>(null);

  // Redirect to original destination after login
  const from = (location.state as { from?: string })?.from || '/';

  // Check for OAuth error from redirect
  const oauthError = searchParams.get('error');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  // Read and clear any stored auth error (e.g. account deactivated)
  useEffect(() => {
    const stored = localStorage.getItem('auth_error');
    if (stored) {
      setStoredAuthError(stored);
      localStorage.removeItem('auth_error');
    }
  }, []);

  // Clear error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const displayError =
    storedAuthError === 'inactive' || oauthError === 'inactive'
      ? 'Your account has been deactivated.'
      : oauthError === 'social'
        ? 'Failed to sign in. Please try again.'
        : error;

  const handleLoginClick = (provider: 'google' | 'microsoft') => {
    window.umami?.track('login_initiated', { provider });
    setConfirmStep({ step: 0, provider });
  };

  const handleConfirm = () => {
    if (!confirmStep) return;
    if (confirmStep.step < CONFIRM_STEPS.length - 1) {
      window.umami?.track('login_step_passed', { step: confirmStep.step + 1, provider: confirmStep.provider });
      setConfirmStep({ ...confirmStep, step: confirmStep.step + 1 });
    } else {
      window.umami?.track('login_completed', { provider: confirmStep.provider });
      setConfirmStep(null);
      setIsPending(true);
      if (confirmStep.provider === 'google') loginWithGoogle(from);
      else loginWithMicrosoft(from);
    }
  };

  const activeStep = confirmStep !== null ? CONFIRM_STEPS[confirmStep.step] : null;

  return (
    <div className="flex items-center justify-center py-8 sm:py-16">
      {/* Confirmation modal */}
      <AnimatePresence>
        {confirmStep !== null && activeStep !== null && (
          <motion.div
            key="modal-backdrop"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              key="modal-card"
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center space-y-5"
              initial={{ scale: 0.8, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={confirmStep.step}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  <motion.div
                    className="text-5xl"
                    initial={{ rotate: -20, scale: 0.6 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 14 }}
                  >
                    {activeStep.emoji}
                  </motion.div>
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight">{activeStep.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{activeStep.body}</p>
                  <div className="text-xs text-gray-400 font-mono">{confirmStep.step + 1} / {CONFIRM_STEPS.length}</div>
                  <div className="flex flex-col gap-2 pt-1">
                    <motion.button
                      onClick={handleConfirm}
                      className="w-full py-2.5 px-4 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                      whileTap={{ scale: 0.97 }}
                    >
                      {activeStep.yes}
                    </motion.button>
                    <button
                      onClick={() => {
                        window.umami?.track('login_abandoned', { step: confirmStep.step, provider: confirmStep.provider });
                        setConfirmStep(null);
                      }}
                      className="w-full py-2.5 px-4 bg-white text-gray-400 text-sm rounded-xl hover:text-gray-600 hover:bg-gray-50 transition-colors focus:outline-none"
                    >
                      {activeStep.no}
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div
        className="max-w-md w-full space-y-8 p-6 sm:p-8 bg-white rounded-lg shadow"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
      >
        <div>
          <h2 className="text-center text-3xl font-bold text-gray-900">Sign in</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {from !== '/'
              ? 'Sign in to continue'
              : 'Use your social account to continue'}
          </p>
        </div>

        {displayError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {displayError}
          </div>
        )}

        <div className="space-y-3">
          {/* Google - Active */}
          <button
            onClick={() => handleLoginClick('google')}
            disabled={isLoading || isPending}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading || isPending ? 'Loading...' : 'Sign in with Google'}
          </button>

          {/* Microsoft / Outlook */}
          <button
            onClick={() => handleLoginClick('microsoft')}
            disabled={isLoading || isPending}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 23 23">
              <path fill="#f35325" d="M1 1h10v10H1z" />
              <path fill="#81bc06" d="M12 1h10v10H12z" />
              <path fill="#05a6f0" d="M1 12h10v10H1z" />
              <path fill="#ffba08" d="M12 12h10v10H12z" />
            </svg>
            {isLoading || isPending ? 'Loading...' : 'Sign in with Microsoft'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
